import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import Sidebar from "../components/Sidebar";
import DateRangePicker from "../components/DateRangePicker";
import ChannelSelector from "../components/ChannelSelector";
import EngagementScoreChart from "../components/EngagementScoreChart";
import ActivityHeatmap from "../components/ActivityHeatmap";
import ChannelEngagementTable from "../components/ChannelEngagementTable";
import EmptyState from "../components/EmptyState";
import axios from "axios";
import { getApiBaseUrl } from "../utils/apiConfig";

// Import Slack data service functions
import {
  fetchOrganization,
  checkSlackConnection,
  fetchSlackChannels
} from "../utils/slackDataService";

import SyncButtonWithTimestamp from '../components/SyncButtonWithTimestamp';

const Engagement = () => {
  const navigate = useNavigate();
  
  // State
  const [loading, setLoading] = useState(true);
  const [dataLoading, setDataLoading] = useState(false);
  const [initialLoadComplete, setInitialLoadComplete] = useState(false);
  const [error, setError] = useState(null);
  const [user, setUser] = useState(null);
  const [organization, setOrganization] = useState(null);
  const [slackConnected, setSlackConnected] = useState(false);
  const [availableChannels, setAvailableChannels] = useState([]);
  const [noDataAvailable, setNoDataAvailable] = useState(false);
  
  // Initialize dateRange with "last30days"
  const [dateRange, setDateRange] = useState(() => {
    const today = new Date();
    const startDate = new Date(today);
    startDate.setDate(today.getDate() - 29);
    const endDate = new Date(today);
    return { startDate, endDate, range: "last30days" };
  });
  
  const [selectedChannels, setSelectedChannels] = useState([]);
  
  // Data states
  const [engagementScoreData, setEngagementScoreData] = useState([]);
  const [activityHeatmapData, setActivityHeatmapData] = useState([]);
  const [channelEngagementData, setChannelEngagementData] = useState([]);
  
  // Check user session and load initial data
  useEffect(() => {
    const loadInitialData = async () => {
      // Retrieve user data from localStorage
      const userData = localStorage.getItem("user");
      if (userData && userData !== "undefined") {
        try {
          setLoading(true);
          const parsedUser = JSON.parse(userData);
          setUser(parsedUser);
          
          if (parsedUser.organizations && parsedUser.organizations.length > 0) {
            const firstOrgId = parsedUser.organizations[0].organizationId;
            const org = await fetchOrganization(firstOrgId);
            setOrganization(org);
          }
        } catch (error) {
          console.error("Error parsing user data:", error);
          setError("Error loading user data. Please try again.");
        } finally {
          setLoading(false);
        }
      } else {
        navigate("/login");
      }
    };
    
    loadInitialData();
  }, [navigate]);
  
  // Check Slack connection and load channels
  useEffect(() => {
    const checkSlackAndLoadChannels = async () => {
      if (!organization) return;
      
      try {
        setLoading(true);
        setError(null);
        
        // Check if Slack is connected
        const isConnected = await checkSlackConnection(organization);
        setSlackConnected(isConnected);
        
        if (isConnected) {
          // Load available channels
          const channels = await fetchSlackChannels(organization);
          setAvailableChannels(channels);
        } else {
          setError("Slack is not connected. Please connect Slack in the Integrations page.");
        }
      } catch (error) {
        console.error("Error checking Slack connection:", error);
        setError("Error checking Slack connection. Please try again.");
      } finally {
        setLoading(false);
        setInitialLoadComplete(true);
      }
    };
    
    checkSlackAndLoadChannels();
  }, [organization]);

  // Load engagement data when slackConnected becomes true
  useEffect(() => {
    if (slackConnected && organization && initialLoadComplete) {
      loadEngagementData(dateRange);
    }
  }, [slackConnected, organization, initialLoadComplete]);
  
  // Load engagement data based on filters
  const loadEngagementData = async (currentDateRange = dateRange, channelsOverride = null) => {
    if (!organization || !slackConnected) {
      console.log("Cannot load data: organization or slack not connected.");
      return;
    }
    
    try {
      setDataLoading(true);
      setError(null);
      setNoDataAvailable(false);
      
      // Use channelsOverride if provided, otherwise use selectedChannels state
      const channelsToUse = channelsOverride !== null ? channelsOverride : selectedChannels;
      
      console.log("Loading engagement data with filter:", { 
        dateRange: currentDateRange, 
        selectedChannels: channelsToUse
      });
      
      // Get channel names to filter by (since backend uses names as keys)
      const channelNames = channelsToUse.length > 0 
        ? channelsToUse
        : null;
      
      const token = localStorage.getItem("token");
      const apiUrl = getApiBaseUrl();
      const orgId = organization._id || organization.id;
      
      // Prepare query parameters - removed date filtering to get all data from DB
      const params = {
        rangeType: currentDateRange.range
        // Removed startDate and endDate to get all data from database
      };
      
      if (channelNames && channelNames.length > 0) {
        params.channels = channelNames.join(',');
      }
      
      // Fetch the report directly from the backend
      const response = await axios.get(
        `${apiUrl}/slack-reports/organization/${orgId}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
          params
        }
      );
      
      if (!response.data.success || !response.data.data || !response.data.data.reportData) {
        console.warn('No report data available');
        setNoDataAvailable(true);
        setDataLoading(false);
        return;
      }
      
      const reportData = response.data.data.reportData;
      console.log("Engagement report data:", reportData);
      console.log("Engagement scores keys:", Object.keys(reportData.engagement?.engagementScore || {}));
      console.log("Activity keys:", Object.keys(reportData.engagement?.activity || {}));
      console.log("Channel engagement keys:", Object.keys(reportData.engagement?.channelEngagement || {}));

      // Process engagement score data from new structure
      if (reportData.engagement && reportData.engagement.engagementScore) {
        const engagementScores = reportData.engagement.engagementScore;
        
        // First, calculate message counts per date from channel data
        const messageCountsByDate = {};
        if (reportData.engagement && reportData.engagement.channelEngagement) {
          Object.values(reportData.engagement.channelEngagement).forEach(channelDates => {
            Object.entries(channelDates).forEach(([dateStr, dayData]) => {
              const messageCount = dayData.numberOfMessages || 0;
              messageCountsByDate[dateStr] = (messageCountsByDate[dateStr] || 0) + messageCount;
            });
          });
        }
        
        const timeData = Object.entries(engagementScores).map(([dateStr, score]) => ({
          date: dateStr,
          score: Math.round(score * 100), // Convert from 0-1 to 0-100 scale
          messageCount: messageCountsByDate[dateStr] || 0 // Get actual message count for this date
        })).sort((a, b) => new Date(a.date.split(',')[0]) - new Date(b.date.split(',')[0]));
        
        // Filter by date range on frontend
        const startFilter = new Date(currentDateRange.startDate);
        const endFilter = new Date(currentDateRange.endDate);
        const filteredTimeData = timeData.filter(td => {
          const tdDate = new Date(td.date.split(',')[0]); // Parse "Jun 1 2025, Monday" format
          return tdDate >= startFilter && tdDate <= endFilter;
        });

        console.log("Time data before filtering:", timeData.length);
        console.log("Time data after filtering:", filteredTimeData.length);
        console.log("Date range filter:", startFilter.toDateString(), "to", endFilter.toDateString());

        setEngagementScoreData(filteredTimeData);
      } else {
        console.log("No engagement scores found in reportData");
        setEngagementScoreData([]);
      }
      
      // Process heatmap data from new structure - FILTER BY DATE RANGE
      if (reportData.engagement && reportData.engagement.activity) {
        const activityData = reportData.engagement.activity;
        console.log("Processing activity data:", activityData);
        console.log("Activity data entries:", Object.entries(activityData));
        
        // Filter activity data by current date range
        const startFilter = new Date(currentDateRange.startDate);
        const endFilter = new Date(currentDateRange.endDate);
        
        const filteredActivityData = Object.entries(activityData).filter(([dateStr, hourlyData]) => {
          const dateObj = new Date(dateStr.split(',')[0]); // Parse "Jun 1 2025, Monday" format
          return dateObj >= startFilter && dateObj <= endFilter;
        });
        
        console.log(`Activity data - total: ${Object.keys(activityData).length}, filtered: ${filteredActivityData.length}`);
        console.log("Date range filter:", startFilter.toDateString(), "to", endFilter.toDateString());
        
        const activityLevelMap = {
          'very low': 10,
          'low': 30,
          'medium': 50,
          'high': 70,
          'very high': 85,
          'extreme': 100
        };
        
        // Aggregate by day-of-week + hour, keeping the max value when
        // multiple dates fall on the same weekday (e.g. 4 Saturdays in 30 days)
        const heatmapAggregated = {};
        
        filteredActivityData.forEach(([dateStr, hourlyData]) => {
          console.log(`Processing activity for date ${dateStr}:`, hourlyData);
          
          const dayOfWeek = dateStr.split(',')[1]?.trim() || 'Unknown';
          
          Object.entries(hourlyData).forEach(([hour, level]) => {
            const numericValue = activityLevelMap[level] || 10;
            const formattedHour = hour.includes(':') ? hour : `${hour.padStart(2, '0')}:00`;
            const hourIndex = parseInt(formattedHour.split(':')[0]);
            const key = `${dayOfWeek}-${hourIndex}`;
            
            if (!heatmapAggregated[key] || numericValue > heatmapAggregated[key].value) {
              heatmapAggregated[key] = {
                hour: formattedHour,
                hourIndex: hourIndex,
                day: dayOfWeek,
                value: numericValue,
                level: level,
                date: dateStr
              };
            }
          });
        });
        
        const heatmapData = Object.values(heatmapAggregated);

        console.log("Generated filtered heatmap data:", heatmapData.length, "entries");
        
        // Log sample of filtered data for debugging
        if (heatmapData.length > 0) {
          console.log("Sample heatmap entries:", heatmapData.slice(0, 5));
          const uniqueDays = [...new Set(heatmapData.map(item => item.day))];
          console.log("Unique days in filtered data:", uniqueDays);
        }
        
        setActivityHeatmapData(heatmapData);
      } else {
        console.log("No activity data found in reportData");
        setActivityHeatmapData([]);
      }
      
      // Process channel data from new structure with metadata
      const channelEngagementArray = reportData.engagement?.channelEngagementArray;
      const channelEngagement = reportData.engagement?.channelEngagement;
      
      if (channelEngagementArray && channelEngagementArray.length > 0) {
        console.log("Processing channel engagement array data:", channelEngagementArray);
        
        // Group by channel and filter by date range
        const startFilter = new Date(currentDateRange.startDate);
        const endFilter = new Date(currentDateRange.endDate);
        
        const channelMap = new Map();
        
        channelEngagementArray.forEach(item => {
          const dateObj = new Date(item.date.split(',')[0]); // Parse "Jun 1 2025, Monday" format
          if (dateObj >= startFilter && dateObj <= endFilter) {
            if (!channelMap.has(item.channel)) {
              channelMap.set(item.channel, {
                items: [],
                metadata: {
                  channelId: item.channelId,
                  is_private: item.is_private,
                  num_members: item.num_members,
                  topic: item.topic,
                  creator: item.creator,
                  created: item.created
                }
              });
            }
            channelMap.get(item.channel).items.push(item);
          }
        });
        
        const channelData = Array.from(channelMap.entries()).map(([channelName, channelInfo]) => {
          const { items, metadata } = channelInfo;
          
          console.log(`Processing channel ${channelName}:`, items.length, 'items');
          
          if (items.length === 0) {
            return {
              id: metadata.channelId || channelName.replace('#', ''),
              channel: channelName,
              is_private: metadata.is_private || false,
              num_members: metadata.num_members || 0,
              score: 0,
              activity: 'No data',
              peakActivity: 'N/A',
              messages: 0,
              reactions: 0,
              contributors: 0
            };
          }
          
          // Calculate metrics based on filtered data
          const scores = items.map(item => item.engagementScore || 0);
          const avgScore = scores.length > 0 ? scores.reduce((sum, score) => sum + score, 0) / scores.length : 0;
          
          // Calculate totals for filtered period
          const totalMessages = items.reduce((sum, item) => sum + (item.numberOfMessages || 0), 0);
          const totalReactions = items.reduce((sum, item) => {
            const v =
              item.numberOfReactions ??
              item.reactionCount ??
              item.reaction_count ??
              0;
            return sum + (Number(v) || 0);
          }, 0);
          const totalThreads = items.reduce((sum, item) => {
            const v =
              item.numberOfThreads ??
              item.threads ??
              item.threadCount ??
              0;
            return sum + (Number(v) || 0);
          }, 0);
          const maxContributors = Math.max(...items.map(item => item.numberOfContributors || 0), 0);
          
          // Get most recent activity level from filtered data
          const sortedItems = items.sort((a, b) => {
            return new Date(b.date.split(',')[0]) - new Date(a.date.split(',')[0]);
          });
          const latestActivity = sortedItems[0] ? sortedItems[0].activity || 'low' : 'low';
          
          // Calculate peak activity from this channel's own data
          let peakActivity = 'N/A';
          if (items.length > 0) {
            const peakItem = items.reduce((max, item) =>
              (item.numberOfMessages || 0) > (max.numberOfMessages || 0) ? item : max,
              items[0]
            );

            if (peakItem && (peakItem.numberOfMessages || 0) > 0) {
              const peakDate = peakItem.date;
              const dayOfWeek = peakDate.split(',')[1]?.trim() || '';

              // Cross-reference with global hourly data for this channel's peak date
              const dayHourlyData = reportData.engagement?.activity?.[peakDate];
              if (dayHourlyData) {
                const activityLevelMap = {
                  'very low': 10, 'low': 30, 'medium': 50,
                  'high': 70, 'very high': 85, 'extreme': 100
                };
                let maxHourValue = 0;
                let peakHour = '';
                Object.entries(dayHourlyData).forEach(([hour, level]) => {
                  const numericValue = activityLevelMap[level] || 10;
                  if (numericValue > maxHourValue) {
                    maxHourValue = numericValue;
                    peakHour = hour.includes(':') ? hour : `${hour.padStart(2, '0')}:00`;
                  }
                });
                if (peakHour && maxHourValue > 10) {
                  peakActivity = `${peakHour} on ${dayOfWeek}`;
                } else if (dayOfWeek) {
                  peakActivity = dayOfWeek;
                }
              } else if (dayOfWeek) {
                peakActivity = dayOfWeek;
              }
            }
          }
          
          console.log(`Channel ${channelName} - avgScore: ${avgScore}, totalMessages: ${totalMessages}, latestActivity: ${latestActivity}, peakActivity: ${peakActivity}`);
          
          return {
            id: metadata.channelId || channelName.replace('#', ''),
            channel: channelName,
            is_private: metadata.is_private || false,
            num_members: metadata.num_members || 0,
            score: Math.round(avgScore * 100), // Convert from 0-1 to 0-100 scale
            activity: latestActivity.charAt(0).toUpperCase() + latestActivity.slice(1).replace('_', ' '),
            peakActivity: peakActivity,
            messages: totalMessages,
            reactions: totalReactions,
            threads: totalThreads,
            contributors: maxContributors
          };
        }).sort((a, b) => b.score - a.score);
        
        // Enrich channel data with is_private property from Slack channels API
        try {
          const slackChannels = await fetchSlackChannels(organization);
          const channelPrivateMap = new Map(slackChannels.map(ch => [ch.name, ch.is_private]));
          channelData.forEach(item => {
            if (channelPrivateMap.has(item.channel)) {
              item.is_private = channelPrivateMap.get(item.channel);
            }
          });
        } catch (error) {
          console.warn("Could not fetch channels for is_private property:", error);
        }

        console.log("Generated channel data from array:", channelData.length, "channels");
        setChannelEngagementData(channelData);
      } else if (channelEngagement) {
        // Fallback to old format if new array format is not available
        console.log("Processing channel engagement data (old format):", channelEngagement);
        console.log("Channel engagement entries:", Object.entries(channelEngagement));
        
        const channelData = Object.entries(channelEngagement).map(([channelName, channelDates]) => {
          console.log(`Processing channel ${channelName}:`, channelDates);
          
          // Filter channel dates by the current date range
          const startFilter = new Date(currentDateRange.startDate);
          const endFilter = new Date(currentDateRange.endDate);
          
          const filteredChannelData = Object.entries(channelDates).filter(([dateStr, dayData]) => {
            const dateObj = new Date(dateStr.split(',')[0]); // Parse "Jun 1 2025, Monday" format
            return dateObj >= startFilter && dateObj <= endFilter;
          });
          
          console.log(`Channel ${channelName} - total dates: ${Object.keys(channelDates).length}, filtered dates: ${filteredChannelData.length}`);
          
          if (filteredChannelData.length === 0) {
            return {
              id: channelName.replace('#', ''),
              channel: channelName,
              is_private: false, // Default to false for old format
              num_members: 0,
              score: 0,
              activity: 'No data',
              peakActivity: 'N/A',
              messages: 0,
              reactions: 0,
              contributors: 0
            };
          }
          
          // Calculate metrics based on filtered data (old format logic)
          const scores = filteredChannelData.map(([dateStr, dayData]) => dayData.engagementScore || 0);
          const avgScore = scores.length > 0 ? scores.reduce((sum, score) => sum + score, 0) / scores.length : 0;
          
          const totalMessages = filteredChannelData.reduce((sum, [dateStr, dayData]) => sum + (dayData.numberOfMessages || 0), 0);
          const totalReactions = filteredChannelData.reduce((sum, [dateStr, dayData]) => {
            const v =
              dayData.numberOfReactions ??
              dayData.reactionCount ??
              dayData.reaction_count ??
              0;
            return sum + (Number(v) || 0);
          }, 0);
          const totalThreads = filteredChannelData.reduce((sum, [dateStr, dayData]) => {
            const v =
              dayData.numberOfThreads ??
              dayData.threads ??
              dayData.threadCount ??
              0;
            return sum + (Number(v) || 0);
          }, 0);
          const maxContributors = Math.max(...filteredChannelData.map(([dateStr, dayData]) => dayData.numberOfContributors || 0), 0);
          
          const sortedFilteredDates = filteredChannelData.sort(([dateA], [dateB]) => {
            return new Date(dateB.split(',')[0]) - new Date(dateA.split(',')[0]);
          });
          const latestActivity = sortedFilteredDates[0] ? sortedFilteredDates[0][1].activity || 'low' : 'low';
          
          return {
            id: channelName.replace('#', ''),
            channel: channelName,
            is_private: false, // Default to false for old format
            num_members: 0,
            score: Math.round(avgScore * 100),
            activity: latestActivity.charAt(0).toUpperCase() + latestActivity.slice(1).replace('_', ' '),
            peakActivity: 'N/A',
            messages: totalMessages,
            reactions: totalReactions,
            threads: totalThreads,
            contributors: maxContributors
          };
        }).sort((a, b) => b.score - a.score);
        
        // Enrich channel data with is_private property from Slack channels API
        try {
          const slackChannels = await fetchSlackChannels(organization);
          const channelPrivateMap = new Map(slackChannels.map(ch => [ch.name, ch.is_private]));
          channelData.forEach(item => {
            if (channelPrivateMap.has(item.channel)) {
              item.is_private = channelPrivateMap.get(item.channel);
            }
          });
        } catch (error) {
          console.warn("Could not fetch channels for is_private property:", error);
        }

        console.log("Generated channel data from old format:", channelData.length, "channels");
        setChannelEngagementData(channelData);
      } else {
        console.log("No channel engagement data found in reportData");
        setChannelEngagementData([]);
      }
      
      // Check if we have any real data
      const hasEngagementData = reportData.engagement && 
        (Object.keys(reportData.engagement.engagementScore || {}).length > 0 ||
         Object.keys(reportData.engagement.channelEngagement || {}).length > 0 ||
         (reportData.engagement.channelEngagementArray && reportData.engagement.channelEngagementArray.length > 0));
      
      setNoDataAvailable(!hasEngagementData);
      
    } catch (error) {
      console.error("Error loading engagement data:", error);
      
      // Handle 404 (no report found)
      if (error.response && error.response.status === 404) {
        setNoDataAvailable(true);
        setError("No engagement data available for the selected date range.");
      } else {
        setError("Error loading engagement data. Please try again.");
      }
    } finally {
      setDataLoading(false);
    }
  };
  
  // Handle date range change
  const handleDateRangeChange = useCallback((newRange) => {
    console.log("Date range changed:", newRange);
    
    // Set the new date range state
    setDateRange(newRange);
    
    // Reload data with new date range - pass the newRange directly
    if (organization && slackConnected) {
      loadEngagementData(newRange);
    }
  }, [organization, slackConnected]);
  
  // Handle channel selection change
  const handleChannelChange = (channels) => {
    console.log("Selected channels changed:", channels);
    setSelectedChannels(channels);
    
    // Reload data with new channel selection  
    if (organization && slackConnected) {
      loadEngagementData(dateRange, channels); // Pass the current dateRange and the new channels
    }
  };
  
  return (
    <div className="flex h-screen h-dvh overflow-hidden bg-bg-gradient-dash">
      <Sidebar user={user} organization={organization} />
      
      <div className="flex-1  overflow-y-auto">
        {/* Header and filters */}
        <div className="p-5 pb-0">
          <h1 className="text-xl font-normal text-black-0">Engagement</h1>
        </div>
        
        {/* Slack not connected message */}
        {!slackConnected && !loading && (
          <EmptyState
            variant="no-connection"
            title="Slack Not Connected"
            description="You need to connect to Slack first to view engagement data and analyze your team's activity patterns."
            actionButton={
              <button 
                onClick={() => navigate('/settings')}
                className="bg-brand text-white px-6 py-2 rounded-md hover:bg-brand-dark transition-colors font-medium"
              >
                Go to Settings
              </button>
            }
          />
        )}
        
        {/* Error message */}
        {error && (
          <div className="bg-red-light p-4 rounded-md my-4">
            <p className="text-red-strong">{error}</p>
          </div>
        )}
        
        {/* No data available message */}
        {noDataAvailable && slackConnected && !loading && !dataLoading && (
          <EmptyState
            variant="no-data"
            title="No engagement data available"
            description="There are no Slack messages in the selected date range, or the selected channels don't have any messages. Try selecting a different date range or channels."
            actionButton={null}
          />
        )}
          
        {/* Main content - only show if Slack is connected and data is loaded */}
        {slackConnected && (
          <>
          {/* Filters */}
            <div className="flex items-center justify-between p-5">
              <div className="flex items-center gap-3">
                <DateRangePicker 
                  dateRange={dateRange} 
                  onChange={handleDateRangeChange} 
                />
                <ChannelSelector 
                  channels={availableChannels.map(c => ({ name: c.name || c.channel || "unknown", id: c.id }))} 
                  selectedChannels={selectedChannels}
                  onChange={handleChannelChange}
                />
              </div>
              <SyncButtonWithTimestamp organization={organization} dateRange={dateRange} onSyncComplete={() => window.location.reload()} />
            </div>
          
            {/* Loading indicator for filter changes */}
            {dataLoading && (
              <div className="flex justify-center items-center py-4 mb-4">
                <div className="w-6 h-6 border-2 border-grey-40 border-t-brand rounded-full animate-spin mr-3"></div>
                <span className="text-grey-60">Updating data...</span>
            </div>
          )}
          
            {/* Charts row */}
            <div className="flex gap-4 p-5 pt-0">
              <div className="flex-1">
                <EngagementScoreChart data={engagementScoreData} />
              </div>
              <div className="flex-1">
                <ActivityHeatmap data={activityHeatmapData} />
              </div>
              </div>
              
              {/* Channel engagement table */}
                <ChannelEngagementTable data={channelEngagementData} />
            
            {/* Empty data message when tables/charts are empty */}
            {channelEngagementData.length === 0 && !dataLoading && !noDataAvailable && (
              <div className="mt-4">
                <EmptyState
                  variant="search"
                  title="No channel data matches your filters"
                  description="Try adjusting your date range or channel selection to see engagement data."
                  actionButton={null}
                />
              </div>
            )}
          </>
          )}
      </div>
    </div>
  );
};

export default Engagement; 